import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, deleteDoc, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const UpdateArbor1 = () => {
  const [status, setStatus] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [summary, setSummary] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    checkCurrentEvents();
  }, []);

  const checkCurrentEvents = async () => {
    try {
      const arbor1Ref = collection(db, 'Arbor1');
      const snapshot = await getDocs(arbor1Ref);
      setStatus(`Current Arbor1 collection has ${snapshot.size} events.`);
    } catch (error) {
      setStatus(`Error checking events: ${error.message}`);
    }
  };

  const handleUpdate = async () => {
    if (!currentUser) {
      setStatus('You must be logged in to update events.');
      return;
    }

    setIsUpdating(true);
    setStatus('Starting smart update of Arbor1 events...');
    
    try {
      const arbor1Ref = collection(db, 'Arbor1');
      const classID = 'ArborSchool-1ST(Grade One)';
      
      // Define all events with their updated data
      const updatedEvents = {
        'Year 1 Show Production – An Exciting Summer Adventure': {
          description: 'Please join us for a memorable performance by the children.'
        },
        'Inspired by Nature – Whole School Event': {
          description: 'Parents and families are invited to attend this nature-inspired celebration. Parents will be able to collect their child from class and explore the exhibits together, or remain with the class as they explore together.'
        },
        'Instructional Writing – Home Learning Task': {
          description: 'Children present demonstration to their classmates - please practice at home too!'
        },
        'Year 1 Trip to OliOli!': {
          description: 'Related to Sustainable Toymaker project. Please complete the Zenda payment - where full details regarding lunch, clothing and requirement are also available.'
        },
        'STEAM Fortnight': {
          description: 'Volunteer opportunities available - please see the email for more information.'
        },
        'Parent Talk- BSO Inspection Results Deep Dive': {
          eventName: 'Parent Talk - BSO Inspection Results',
          description: 'Principal shares BSO report feedback'
        },
        'Arbor\'s Got Talent Finals': {
          description: 'Competition finals'
        },
        'Project Landing – \'The Sustainable Toymaker\'': {
          description: 'Children will showcase their finished sustainable toys and share the creative process behind them. Children will also display their writing and ideas developed throughout the project.'
        },
        'Arbor\'s Got Talent Phase 1': {
          description: 'Competition begins - 4 students in year one will move onto the finals after this phase.'
        },
        'Father\'s Day Song – Picture Request': {
          description: 'Special Father\'s Day celebration prep - please send in photos on or before this date.'
        }
      };
      
      // New events to add
      const newEvents = [
        {
          eventName: 'End of Term - Last Day of Academic Year',
          date: new Date(2025, 5, 25),
          day: 25,
          month: 6,
          year: 2025,
          category: 'school',
          description: 'Year end party this day',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'End of Term',
          participants: []
        },
        {
          eventName: 'Deadline for Transport Registration',
          date: new Date(2025, 6, 3),
          day: 3,
          month: 7,
          year: 2025,
          category: 'school',
          description: 'See link: https://forms.office.com/Pages/ResponsePage.aspx?id=5pNODFCb6Uu5F2AYaoSaPs51j7tbIQ9CqI0_O992PVpUOEhSSExLSDJQTE9DM0pIMlJIRjBKU0Y3QS4u - Fill out one form per child',
          hasTime: false,
          isAllDay: true,
          isRecurring: false,
          classID: classID,
          createdAt: new Date().toISOString(),
          status: 'accepted',
          source: 'preloaded',
          emailSubject: 'Transport Registration Deadline',
          participants: []
        }
      ];
      
      // Get all existing events
      const snapshot = await getDocs(arbor1Ref);
      
      let updatedCount = 0;
      let deletedCount = 0;
      let addedCount = 0;
      
      // Update existing events
      for (const docSnapshot of snapshot.docs) {
        const eventData = docSnapshot.data();
        const eventName = eventData.eventName;
        
        // Check if this is the duplicate "Year 1 Project Landing" to delete
        if (eventName === 'Year 1 Project Landing' && eventData.day === 4 && eventData.month === 6) {
          await deleteDoc(doc(db, 'Arbor1', docSnapshot.id));
          deletedCount++;
          continue;
        }
        
        // Check if we have updates for this event
        if (updatedEvents[eventName]) {
          const updates = updatedEvents[eventName];
          await updateDoc(doc(db, 'Arbor1', docSnapshot.id), updates);
          updatedCount++;
        }
      }
      
      // Add new events
      for (const event of newEvents) {
        await addDoc(arbor1Ref, event);
        addedCount++;
      }
      
      const summaryData = {
        updated: updatedCount,
        added: addedCount,
        deleted: deletedCount,
        total: snapshot.size + addedCount - deletedCount
      };
      
      setSummary(summaryData);
      setStatus('Update completed successfully!');
      
    } catch (error) {
      setStatus(`Error: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Update Arbor1 Collection Events</h1>
      <p>This will update all events in the shared Arbor1 collection. Changes will be visible to all Arbor School Grade 1 users.</p>
      
      <div style={{
        backgroundColor: '#f0f8ff',
        padding: '15px',
        borderRadius: '5px',
        marginBottom: '20px'
      }}>
        <h3>What this update will do:</h3>
        <ul>
          <li>Update descriptions for 10 existing events</li>
          <li>Add 2 new events (End of Term & Transport Registration)</li>
          <li>Remove the duplicate "Year 1 Project Landing" event</li>
          <li>Keep all other event data intact</li>
        </ul>
      </div>
      
      <button 
        onClick={handleUpdate} 
        disabled={isUpdating || !currentUser}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: isUpdating || !currentUser ? '#ccc' : '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: isUpdating || !currentUser ? 'not-allowed' : 'pointer',
          marginTop: '20px'
        }}
      >
        {isUpdating ? 'Updating...' : 'Update Arbor1 Events'}
      </button>
      
      {status && (
        <div style={{
          marginTop: '20px',
          padding: '10px',
          backgroundColor: status.includes('Success') || status.includes('completed') ? '#d4edda' : 
                          status.includes('Error') ? '#f8d7da' : '#cce5ff',
          color: status.includes('Success') || status.includes('completed') ? '#155724' : 
                 status.includes('Error') ? '#721c24' : '#004085',
          borderRadius: '5px'
        }}>
          {status}
        </div>
      )}
      
      {summary && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#e8f5e9',
          borderRadius: '5px'
        }}>
          <h3>Update Summary:</h3>
          <ul>
            <li>✏️ Updated: {summary.updated} events</li>
            <li>➕ Added: {summary.added} new events</li>
            <li>🗑️ Deleted: {summary.deleted} duplicate events</li>
            <li>📊 Total events now: {summary.total}</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default UpdateArbor1;